import { useEffect, useState } from "react";
import { StoreLeaderboard } from "@/components/StoreLeaderboard";
import { StoreTrendPanel } from "@/components/StoreTrendPanel";
import { PartnerLeaderboard } from "@/components/PartnerLeaderboard";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, LogOut, ClipboardList, Contact, FileSpreadsheet, Megaphone, Mail, Users, MessageCircle, Trophy } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import api from "@/lib/api";
import { StatsGrid, ChartCard, PlaysWinsBar, KpiCard, WaGlobalCard, LandingOverviewCard } from "@/components/StatsCharts";
import Logo from "@/components/Logo";
import AiStatsPanel from "@/components/AiStatsPanel";
import { AlertsPanel } from "@/components/AlertsPanel";
import { WrongStorePanel } from "@/components/WrongStorePanel";
import { RankDiscountsPanel } from "@/components/RankDiscountsPanel";
import { ReferralsPanel } from "@/components/ReferralsPanel";

export default function StatsDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const can = (p) => user?.role === "admin" || (user?.permissions || []).includes(p);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.statsOverview().then(setData).catch(() => toast.error("Errore nel caricamento delle statistiche"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- caricamento iniziale; api/toast sono moduli stabili

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button data-testid="stats-back-button" variant="ghost" size="icon" onClick={() => navigate("/")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          {can("customers") && <Button data-testid="stats-customers-button" variant="outline" size="sm" onClick={() => navigate("/clienti")} className="ml-auto gap-1.5 order-last border-border">
            <Users className="h-4 w-4" /> Clienti
          </Button>}
          {can("audit") && <Button data-testid="stats-audit-button" variant="outline" size="sm" onClick={() => navigate("/log-attivita")} className="gap-1.5 order-last border-border">
            <ClipboardList className="h-4 w-4" /> Log Attività
          </Button>}
          {can("stores") && <Button data-testid="stats-stores-button" variant="outline" size="sm" onClick={() => navigate("/sedi")} className="gap-1.5 order-last border-border">Punti vendita</Button>}
          {can("whatsapp") && <Button data-testid="stats-campaigns-button" variant="outline" size="sm" onClick={() => navigate("/campagne")} className="gap-1.5 order-last border-border">Campagne</Button>}
          <Button data-testid="stats-logout-button" variant="ghost" size="sm" onClick={logout} className="gap-1.5 order-last" title={user?.email}>
            <LogOut className="h-4 w-4" /> Esci
          </Button>
          <Logo className="h-10" />
          <div>
            <h1 className="font-display font-bold text-xl">Cruscotto Statistiche</h1>
            <p className="text-xs text-muted-foreground">Giocate e vincite di tutti i concorsi</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8" data-testid="stats-dashboard">
        {!data ? (
          <p className="text-muted-foreground">Caricamento…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <KpiCard label="Concorsi totali" value={data.contests_total} testId="kpi-contests" />
              <KpiCard label="Concorsi attivi" value={data.contests_active} accent testId="kpi-active" />
            </div>
            <div className="mb-5">
              <ChartCard title="Per concorso" subtitle="Giocate e vincite per ogni iniziativa" testId="chart-contest">
                <PlaysWinsBar data={data.by_contest} layout="vertical" />
              </ChartCard>
            </div>
            <StatsGrid data={data} exportUrl={api.exportAllRedemptionsUrl()} />
            <StoreLeaderboard contests={data.by_contest} isAdmin={user?.role === "admin"} />
            <StoreTrendPanel />
            {user?.role === "admin" && <PartnerLeaderboard />}
            <div className="mt-5"><WaGlobalCard wa={data.whatsapp_delivery} /></div>
            <div className="mt-5"><LandingOverviewCard contests={data.by_contest} /></div>
            <ContactsExport contests={data.by_contest} />
            <AiStatsPanel />
            {user?.role === "admin" && <AlertsPanel />}
            {user?.role === "admin" && <WrongStorePanel />}
            {user?.role === "admin" && <RankDiscountsPanel />}
            {user?.role === "admin" && <ReferralsPanel contests={data.by_contest} />}
          </>
        )}
      </main>
    </div>
  );
}

function ContactsExport({ contests }) {
  const [cid, setCid] = useState("");
  const navigate = useNavigate();
  const { user } = useAuth();
  return (
    <Card className="p-5 bg-card border-border mt-5" data-testid="contacts-export">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px]">
          <h3 className="font-display font-bold flex items-center gap-2"><Contact className="h-4 w-4" /> Contatti marketing</h3>
          <p className="text-xs text-muted-foreground">Numeri WhatsApp ed email raccolti, con colonna Consenso Sì/No (usa solo i contatti con consenso per le campagne).</p>
        </div>
        <select data-testid="contacts-contest-select" value={cid} onChange={(e) => setCid(e.target.value)} className="h-10 rounded-md border border-border bg-background px-3 text-sm">
          <option value="">Tutti i concorsi</option>
          {(contests || []).map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <a href={api.exportContactsUrl(cid)} data-testid="contacts-export-button">
          <Button variant="secondary" className="gap-2"><FileSpreadsheet className="h-4 w-4" /> Contatti marketing (Excel)</Button>
        </a>
        {user?.role === "admin" && (
          <Button data-testid="open-campaigns-button" onClick={() => navigate("/campagne")} className="gap-2"><Megaphone className="h-4 w-4" /> Campagne WhatsApp</Button>
        )}
        {user?.role === "admin" && (
          <Button data-testid="weekly-report-button" variant="outline" className="gap-2 border-border" onClick={async () => {
            if (!window.confirm("Inviare ora il report settimanale a tutti gli admin?")) return;
            try {
              const r = await api.sendWeeklyReport();
              toast.success(`Report inviato a ${r.sent.length} admin (${r.plays} giocate, ${r.wins} vincite)`);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Invio non riuscito");
            }
          }}><Mail className="h-4 w-4" /> Invia report ora</Button>
        )}
        {user?.role === "admin" && (
          <Button data-testid="store-rank-whatsapp-button" variant="outline" className="gap-2 border-border" onClick={async () => {
            if (!window.confirm("Inviare ora via WhatsApp la classifica del mese a tutte le sedi con numero configurato?")) return;
            try {
              const r = await api.sendStoreWeeklyRank();
              toast.success(`Classifica inviata a ${r.sent.length} sedi${r.skipped.length ? ` · ${r.skipped.length} senza dati` : ""}`);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Invio non riuscito");
            }
          }}><MessageCircle className="h-4 w-4" /> Classifica WA alle sedi</Button>
        )}
        {user?.role === "admin" && (
          <Button data-testid="customer-rank-whatsapp-button" variant="outline" className="gap-2 border-border" onClick={async () => {
            if (!window.confirm("Inviare ora via WhatsApp ai 5 partecipanti più attivi del mese scorso (solo con consenso marketing) la loro posizione?")) return;
            try {
              const r = await api.sendCustomerMonthlyRank();
              toast.success(`Classifica clienti ${r.month}: ${r.sent.length} inviate${r.skipped.length ? ` · ${r.skipped.length} senza consenso` : ""}`);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Invio non riuscito");
            }
          }}><Trophy className="h-4 w-4" /> Classifica clienti WA</Button>
        )}
      </div>
    </Card>
  );
}
